import { useEffect, useState } from "react";
import { Package, Loader2, Save, Star, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const PlanEditor = ({ plan, onSaved }) => {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setFeature = (i, v) => set("features", form.features.map((f, j) => (j === i ? v : f)));
  const addFeature = () => set("features", [...form.features, ""]);
  const removeFeature = (i) => set("features", form.features.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/plans/${plan.plan_id}`, {
        method: "PUT",
        headers: { ...(await authHeaders()), "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          tagline: form.tagline,
          monthly: parseFloat(form.monthly) || 0,
          yearly: parseFloat(form.yearly) || 0,
          features: form.features,
          trial_days: parseInt(form.trial_days, 10) || 0,
          highlight: form.highlight,
          active: form.active,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      toast.success(`${form.name} saved`);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.message || "Couldn't save");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={`plan-editor-${plan.plan_id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-lg capitalize">{plan.plan_id}</h3>
          {form.highlight && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
          {!form.active && <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600">hidden</span>}
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="gap-1.5 rounded-full" data-testid={`plan-save-${plan.plan_id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid={`plan-name-${plan.plan_id}`} />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label>Monthly price ({plan.currency})</Label>
          <Input type="number" step="0.01" value={form.monthly} onChange={(e) => set("monthly", e.target.value)} className="mt-1" data-testid={`plan-monthly-${plan.plan_id}`} />
        </div>
        <div>
          <Label>Yearly price ({plan.currency})</Label>
          <Input type="number" step="0.01" value={form.yearly} onChange={(e) => set("yearly", e.target.value)} className="mt-1" data-testid={`plan-yearly-${plan.plan_id}`} />
        </div>
        <div>
          <Label>Free trial (days)</Label>
          <Input type="number" value={form.trial_days} onChange={(e) => set("trial_days", e.target.value)} className="mt-1" data-testid={`plan-trial-${plan.plan_id}`} />
        </div>
      </div>

      <div className="mt-4">
        <Label>Features</Label>
        <div className="space-y-2 mt-1">
          {form.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={f} onChange={(e) => setFeature(i, e.target.value)} className="flex-1" />
              <button onClick={() => removeFeature(i)} className="text-muted-foreground hover:text-red-500 p-1" data-testid={`plan-feature-remove-${plan.plan_id}-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addFeature} className="gap-1.5 rounded-full" data-testid={`plan-feature-add-${plan.plan_id}`}>
            <Plus className="w-3.5 h-3.5" /> Add feature
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-6 mt-5">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={form.highlight} onCheckedChange={(v) => set("highlight", v)} data-testid={`plan-highlight-${plan.plan_id}`} /> Most popular
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid={`plan-active-${plan.plan_id}`} /> Visible to customers
        </label>
      </div>
    </div>
  );
};

const AdminPackages = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/plans`, { headers: await authHeaders() });
      const data = await res.json();
      setPlans(data.plans || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-packages-section">
      <div className="flex items-center gap-2 mb-1">
        <Package className="w-5 h-5" />
        <h2 className="text-xl font-bold">Packages</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Edit your subscription plans — pricing, features and the free-trial length. Changes apply to new checkouts immediately.</p>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-5">
          {plans.map((p) => (
            <PlanEditor key={p.plan_id} plan={p} onSaved={() => load()} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
